// The AI opponent.
//
// One-ply search: clone the state, apply each legal action, let the engine
// settle, and score the result. That is only as good as the evaluation, but it
// has a property that matters more than depth here -- the AI plays through the
// same `legalActions`/`applyAction` interface a human does, so it cannot see
// hidden information or take an action a player could not.
//
// Riftbound rewards board presence and position over card advantage: points come
// from holding battlefields, so the evaluation weights "who is standing where"
// far above "who has more cards".

import {
  advance, applyAction, legalActions,
} from '../rules/engine.ts';
import { nextInt } from '../rules/rng.ts';
import type { Rules } from '../rules/state.ts';
import {
  OPPONENT, abilitiesOf, battlefieldLocation, boardObjects, canPay, effectiveMight,
  poolSize, tapIsDestructive, totalCost, unitsOf, zoneList,
} from '../rules/state.ts';
import { isUnitType } from '../rules/cards.ts';
import { DOMAINS } from '../rules/types.ts';
import type {
  Action, Cost, Domain, GameObject, GameState, PlayerId, RunePool, SpendPurpose,
} from '../rules/types.ts';

export type Difficulty = 'easy' | 'normal' | 'hard';

export interface AiOptions {
  difficulty?: Difficulty;
  /** Wall-clock budget per decision. The search stops early rather than stall a frame. */
  budgetMs?: number;
  /**
   * The AI's own randomness. It must not be the GameState, because drawing from
   * the game's seed would make the shuffle depend on how the AI deliberated and
   * destroy replayability.
   */
  rng?: { seed: number };
  /**
   * Filled in with what the search did, for the match log's "why".
   *
   * An out-parameter rather than a richer return so that every caller that only
   * wants the move -- the arena, the self-play harness, the session -- is
   * unchanged, and so that collecting it costs nothing when nobody asks.
   */
  trace?: AiTrace;
}

/** Why the AI picked what it picked, as far as its search can say. */
export interface AiTrace {
  considered: number;
  evaluated: number;
  score: number;
  runnerUp: number;
  blundered: boolean;
}

interface Weights {
  point: number;
  nearVictory: number;
  battlefield: number;
  contested: number;
  unitAtBattlefield: number;
  mightAtBattlefield: number;
  mightInBase: number;
  card: number;
  /** 164.2. What a rune is worth standing on the board at all, ready or not. */
  rune: number;
  readyRune: number;
  pool: number;
  /** How often the AI takes a deliberately suboptimal action. */
  blunderRate: number;
  /** How many candidate actions to evaluate at most. */
  breadth: number;
}

/** Fallback randomness when a caller does not supply its own stream. */
const defaultRng = { seed: 0x9e3779b9 };

const WEIGHTS: Record<Difficulty, Weights> = {
  easy: {
    point: 40, nearVictory: 0, battlefield: 8, contested: 1,
    unitAtBattlefield: 2, mightAtBattlefield: 0.5, mightInBase: 0.2,
    card: 1, rune: 0.9, readyRune: 0.3, pool: 0.1, blunderRate: 0.35, breadth: 12,
  },
  normal: {
    point: 60, nearVictory: 6, battlefield: 16, contested: 3,
    unitAtBattlefield: 4, mightAtBattlefield: 1.2, mightInBase: 0.4,
    card: 2, rune: 2.2, readyRune: 0.8, pool: 0.25, blunderRate: 0.10, breadth: 28,
  },
  // Hard shares normal's weights deliberately. Those are the tuned ones; hard
  // differs by never blundering and by getting enough search budget to actually
  // look at every candidate, which is where its edge comes from. Scaling the
  // weights up instead made it measurably worse, because it overvalued board
  // presence relative to points.
  hard: {
    point: 60, nearVictory: 6, battlefield: 16, contested: 3,
    unitAtBattlefield: 4, mightAtBattlefield: 1.2, mightInBase: 0.4,
    card: 2, rune: 2.2, readyRune: 0.8, pool: 0.25, blunderRate: 0.0, breadth: 64,
  },
};

/**
 * Scores a position from `me`'s point of view. Symmetric: the opponent's
 * position is subtracted, so an action that helps them as much as it helps you
 * is correctly valued at zero.
 */
export function evaluate(
  state: GameState, rules: Rules, me: PlayerId, w: Weights = WEIGHTS.normal,
): number {
  if (state.winner !== null) return state.winner === me ? 1e6 : -1e6;

  let score = 0;
  for (const side of [me, OPPONENT(me)] as PlayerId[]) {
    const sign = side === me ? 1 : -1;
    const player = state.players[side];

    score += sign * player.points * w.point;
    // Points get more valuable the closer they are to ending the game.
    const gap = state.config.victoryScore - player.points;
    if (gap <= 3) score += sign * (4 - gap) * w.nearVictory;

    score += sign * zoneList(state, 'hand', side).length * w.card;
    score += sign * poolSize(player.pool) * w.pool;

    for (const bf of state.battlefields) {
      if (bf.controller === side) score += sign * w.battlefield;
      if (bf.contestedBy === side) score += sign * w.contested;

      const units = unitsOf(state, rules.index, side, battlefieldLocation(bf.index));
      score += sign * units.length * w.unitAtBattlefield;
      for (const u of units) {
        score += sign * effectiveMight(state, rules, u) * w.mightAtBattlefield;
      }
    }

    for (const o of boardObjects(state)) {
      if (o.controller !== side) continue;
      const def = rules.index.get(o.card);
      if (def.type === 'rune') {
        // 164.2. The two rune taps are not interchangeable, and the evaluation
        // has to be able to tell them apart. `[E]` exhausts a rune for Energy
        // and it comes back next turn; "Recycle this" sends it to the Rune
        // Deck for Power and it is gone for the game.
        //
        // Scoring only *ready* runes made both look identical -- an exhausted
        // rune and a destroyed one are each worth nothing -- so the AI would
        // recycle runes outright to pay costs that exhausting alone covered.
        // Standing on the board is the larger half of a rune's worth.
        score += sign * w.rune;
        if (!o.exhausted) score += sign * w.readyRune;
      }
      if (isUnitType(def.type) && o.location?.kind === 'base') {
        score += sign * effectiveMight(state, rules, o) * w.mightInBase;
      }
    }
  }
  return score;
}

/**
 * Picks an action for the player the engine is currently waiting on.
 *
 * Passing is evaluated like anything else rather than treated as a fallback:
 * in a showdown, holding priority is often correct, and an AI that always acts
 * when it can will throw away removal into an empty board.
 */
export function chooseAction(
  state: GameState, rules: Rules, opts: AiOptions = {},
): Action | null {
  const difficulty = opts.difficulty ?? 'normal';
  const w = WEIGHTS[difficulty];
  // The budget has to scale with breadth, or a wider search just gets cut off
  // before it reaches the quiet actions that prioritise() puts last.
  const budget = opts.budgetMs ?? (difficulty === 'hard' ? 120 : 40);

  const waiting = state.waiting;
  if (waiting.kind === 'gameOver' || waiting.kind === 'none') return null;
  const me = waiting.player;

  const all = legalActions(state, rules)
    .filter((a) => a.t !== 'concede')
    // 167.1. A tap that no card could use is a rune spent for nothing -- and a
    // recycled one is a rune off the board for the rest of the game.
    .filter((a) => a.t !== 'activate' || tapIsUseful(state, rules, me, a));
  if (all.length === 0) return null;
  if (all.length === 1) return all[0]!;

  // The engine offers one canonical damage order; there is nothing to search.
  if (waiting.kind === 'assignDamage') return all[0]!;

  const shortlist = prioritise(all, w.breadth, rules, state);

  const started = Date.now();
  let best = shortlist[0]!;
  let bestScore = -Infinity;
  let runnerUp = -Infinity;
  let evaluated = 0;

  for (const action of shortlist) {
    if (Date.now() - started > budget && bestScore > -Infinity) break;

    const copy = structuredClone(state);
    // Events are only useful to the renderer and grow without bound in search.
    copy.events = [];
    if (!applyAction(copy, rules, action)) continue;
    advance(copy, rules);
    evaluated++;

    let score = evaluate(copy, rules, me, w);
    // Nudge away from ending the turn while there is still tempo to spend.
    if (action.t === 'endTurn' || action.t === 'pass') score -= 0.5;

    if (score > bestScore) {
      runnerUp = bestScore;
      bestScore = score;
      best = action;
    } else if (score > runnerUp) {
      runnerUp = score;
    }
  }

  // The trace is what the match log shows when it says why the opponent moved.
  // Filled rather than returned so every existing caller is untouched.
  if (opts.trace) {
    opts.trace.considered = shortlist.length;
    opts.trace.evaluated = evaluated;
    opts.trace.score = bestScore;
    opts.trace.runnerUp = runnerUp;
    opts.trace.blundered = false;
  }

  if (w.blunderRate > 0) {
    const rng = opts.rng ?? defaultRng;
    if (nextInt(rng, 1000) < w.blunderRate * 1000) {
      if (opts.trace) opts.trace.blundered = true;
      return shortlist[nextInt(rng, shortlist.length)] ?? best;
    }
  }
  return best;
}

/**
 * 167.1. Whether tapping for resources could actually buy something.
 *
 * The pool empties at the start of every Main Phase and again at the end of
 * every turn, and whatever is in it is lost. So a tap is only worth making as
 * part of paying for something: the client's own payment search never taps
 * except toward a specific card, and without the same discipline the AI taps
 * for the sake of tapping.
 *
 * That is not a small waste. Over ten games it recycled 364 runes and threw
 * away 377 Power unspent -- better than one wasted Power per rune destroyed --
 * so its board never grew past three runes while the average card costs four
 * and a half resources. Both sides sat broke with a dozen cards in hand, and
 * whoever drew the cheaper curve ran away with it.
 *
 * Anything that is not a plain resource ability is left alone: those are real
 * abilities with real effects, and the search should judge them on those.
 *
 * Exported for the test that holds it to 164.2.b.1. Driving a whole search to
 * reach this decision does not test it: the search is free to prefer some
 * other action, and a test that passes whether or not the rule is there is a
 * test of nothing -- which is exactly what the first attempt turned out to be.
 */
export function tapIsUseful(
  state: GameState, rules: Rules, me: PlayerId,
  action: Extract<Action, { t: 'activate' }>,
): boolean {
  const source = state.objects[action.object];
  if (!source) return false;

  const ability = abilitiesOf(rules, source.card).find(
    (a) => a.kind === 'activated' && a.key === action.ability,
  );
  if (!ability || ability.kind !== 'activated') return true;
  // Only resource abilities are second-guessed; everything else does something.
  const adds = ability.effects.length === 1 && ability.effects[0]?.op === 'addResource'
    ? ability.effects[0] : null;
  if (!adds) return true;

  const player = state.players[me];

  // What is still on the table to be tapped, this one included. Each ready
  // rune is worth one resource -- Energy by exhausting or Power by recycling,
  // never both -- so the reach of a turn is the pool plus that count.
  let taps = 0;
  for (const o of boardObjects(state)) {
    if (o.controller !== me || o.exhausted) continue;
    if (rules.index.get(o.card).type === 'rune') taps++;
  }
  const reach = player.pool.energy + poolSize(player.pool) - player.pool.energy + taps;

  // 164.2. A recycle is a rune off the board for the rest of the game, and
  // exhausting yields Energy while only recycling yields Power. So a recycle is
  // worth making *only* when something actually needs Power that the pool
  // cannot already cover -- otherwise the AI dismantles its own board to float
  // Energy it could have had for free, and 167.1 then throws the surplus away
  // at the end of the phase. That is how a turn ended with no runes left.
  const destructive = tapIsDestructive(state, rules, action);
  // 164.2.b.1. A recycle yields Power of the rune's *own* domain, so which
  // rune is destroyed decides what the Power can pay for. Asking only whether
  // something needs Power is not enough: recycling a Fury rune for a cost that
  // needs Body leaves the shortfall exactly where it was, which re-justifies
  // the next recycle, and the next. That is how a turn ended with the board
  // dismantled and three unusable Power floating -- the AI kept paying a price
  // that could never buy the thing it was paying for.
  const yields = destructive ? tapYields(state, rules, source, adds) : null;

  // A tap is a step on a path, not the whole path: it is worth making when
  // something in hand is out of reach of the pool alone but inside the reach
  // of the pool plus the runes still standing. Requiring the tap to *complete*
  // a cost by itself means a five-cost card is never started, and the AI sits
  // on twelve runes with a hand it never plays.
  for (const id of zoneList(state, 'hand', me)) {
    const card = state.objects[id];
    if (!card) continue;
    const def = rules.index.get(card.card);
    const cost = totalCost(state, rules, def, me);
    if (canPay(player.pool, cost, purposeOf(def.type))) continue;
    if (cost.energy + cost.power > reach) continue;
    if (destructive) {
      if (powerShortfall(player.pool, cost) <= 0) continue;
      // ... and this rune's Power has to be Power this cost will accept.
      if (!payableWith(cost, yields)) continue;
    }
    return true;
  }
  return false;
}

/**
 * The domains a resource ability would produce Power in, or null for "any".
 *
 * `self` is the source's own domain (164.2.b.1), which is what makes a rune's
 * recycle specific to that rune; a named domain is itself; anything else --
 * including an ability that adds only Energy -- imposes no restriction.
 */
function tapYields(
  state: GameState, rules: Rules, source: GameObject, adds: { domain?: string; power?: number },
): Domain[] | null {
  if (!adds.power) return null;
  if (adds.domain === 'self') return [...rules.index.get(source.card).domains];
  if (adds.domain && adds.domain !== 'any') return [adds.domain as Domain];
  return null;
}

/** Whether Power in `yields` could pay this cost's Power at all. */
function payableWith(cost: Cost, yields: Domain[] | null): boolean {
  // No restriction on either side: any Power will do.
  if (yields === null || yields.length === 0) return true;
  if (cost.powerDomains.length === 0) return true;
  return yields.some((d) => cost.powerDomains.includes(d));
}

/** How much of a cost's Power the pool still cannot cover. */
function powerShortfall(pool: RunePool, cost: Cost): number {
  const domains = cost.powerDomains.length > 0 ? cost.powerDomains : DOMAINS;
  let have = pool.any;
  for (const d of domains) have += pool.power[d];
  return cost.power - have;
}

/** 204. Which spending restriction a card's type falls under. */
function purposeOf(type: string): SpendPurpose {
  return type === 'spell' || type === 'signatureSpell' ? 'spell' : 'other';
}

/**
 * Orders the candidate list, and trims it when it is large.
 *
 * The order is the important half, and it used to be skipped whenever the list
 * fitted -- which is almost always: over a thousand real decisions the list
 * exceeded the normal breadth exactly once. That left the *engine's*
 * enumeration order deciding what a 25ms budget looks at, and at ~11ms a
 * candidate that is the first two entries and nothing else.
 *
 * So a bad move that happens to be enumerated early is a bad move the AI makes.
 * Playing cards and moving units change the game; passing rarely does; and a
 * tap that destroys its own source is a last resort whatever it pays for.
 */
function prioritise(
  actions: Action[], breadth: number, rules: Rules, state: GameState,
): Action[] {
  const rank = (a: Action): number => {
    switch (a.t) {
      case 'play': return 0;
      // 164.2. Exhausting a rune and recycling one both "add resources", and
      // the second spends the rune for the rest of the game. Ranked apart so
      // the cheap one is the one a shallow search reaches: this is the same
      // call the client's own payment search makes.
      case 'activate': return tapIsDestructive(state, rules, a) ? 4 : 1;
      case 'move': return 2;
      case 'mulligan': return 3;
      default: return 5;
    }
  };
  // 355.8. Targets are chosen by enumeration, so one spell can appear a dozen
  // times with a different aim each. Sorting alone would let that one card eat
  // the whole budget, so the list is dealt out round-robin: every card and
  // ability gets a first offer before any gets a second.
  const sorted = [...actions].sort((a, b) => rank(a) - rank(b));
  const byObject = new Map<string, Action[]>();
  for (const a of sorted) {
    const key = `${rank(a)}:${'object' in a ? a.object : ''}:${'ability' in a ? a.ability : ''}`;
    const list = byObject.get(key);
    if (list) list.push(a); else byObject.set(key, [a]);
  }
  const dealt: Action[] = [];
  for (let round = 0; dealt.length < sorted.length; round++) {
    let any = false;
    for (const list of byObject.values()) {
      const a = list[round];
      if (a !== undefined) { dealt.push(a); any = true; }
    }
    if (!any) break;
  }
  // Ordered whether or not it needs trimming.
  if (dealt.length <= breadth) return dealt;

  const head = dealt.slice(0, breadth - 2);
  // Always keep the option of doing nothing.
  for (const a of actions) {
    if ((a.t === 'pass' || a.t === 'endTurn') && !head.includes(a)) head.push(a);
  }
  void state;
  return head;
}

/** Runs the AI until control returns to the other player or the game ends. */
export function playTurn(
  state: GameState, rules: Rules, me: PlayerId, opts: AiOptions = {},
): number {
  let steps = 0;
  while (
    state.waiting.kind !== 'gameOver'
    && 'player' in state.waiting
    && state.waiting.player === me
    && steps < 400
  ) {
    const action = chooseAction(state, rules, opts);
    if (!action) break;
    if (!applyAction(state, rules, action)) break;
    advance(state, rules);
    steps++;
  }
  return steps;
}
